import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { NestConfig } from '../sim/contracts';
import { readTune } from '../ui/TuneStore';

const QUEEN_URL = '/assets/queen-ant.glb';
const QUEEN_TUNE_ID = 'queen-ant';
const QUEEN_TUNE_DEFAULTS = { scale: 0.1, rotX: 0, rotY: 0, rotZ: 0, offY: 0 };
const WORKER_URL = '/assets/worker-ant.glb';

/**
 * Ant-farm view: the whole nest sits on Z=0 and the camera looks straight
 * along -Z. Ants render at Z=+ANT_Z_LIFT so they float in front of the
 * translucent green tubes instead of vanishing inside them. Tube radii go up
 * to ~0.36 near the surface, so the lift needs to clear that with margin.
 */
const ANT_Z_LIFT = 0.5;

/**
 * Procedural underground nest: a wild, asymmetric, upside-down tree of
 * translucent green tubes. Background goes pitch black while we're down here
 * (set by NestMode) so the glowing tubes are the only thing to see.
 *
 * Graph:
 *   - Root = entry chamber at the surface.
 *   - Trunk drops into a hub.
 *   - From the hub we recurse — each call draws 2–4 child tubes spraying
 *     outward + downward with random angle/length. Depth caps at ~4.
 *   - Some nodes are promoted to food chambers (wider dome + sugar-crystal pile).
 *   - The deepest / farthest node becomes the queen's chamber.
 */

interface NodeRec {
  pos: THREE.Vector3;
  depth: number;
  kind: 'entry' | 'hub' | 'tunnel' | 'food' | 'leaf' | 'queen';
  radius: number;  // chamber radius (0 = bare tunnel junction)
}

interface Resident {
  /** World-local position, written by the curve evaluator every tick. */
  x: number;
  z: number;
  y: number;
  path: number[];           // BFS path of node indices
  pathIdx: number;          // index into `path` of the node we're currently walking TOWARD
  t: number;                // 0..1 progress along the edge path[pathIdx-1] → path[pathIdx]
  edgeLength: number;       // cached length of the current curve (for speed scaling)
  speed: number;            // metres per second
  hp: number;
  kind: 'worker' | 'guard';
  /** 'wander' residents roam randomly. 'carrier' nurses shuttle eggs between
   *  the queen chamber and the other chambers. */
  role: 'wander' | 'carrier';
  /** Carrier sub-state — which leg of the egg-shuttle they're on. */
  carrierState: 'toQueen' | 'toDeposit';
  /** Visible egg held in mandibles — toggled on when the ant is carrying. */
  eggMesh: THREE.Mesh | null;
  mesh: THREE.Mesh;
  hitPulseT: number;
  retargetT: number;
}

export class NestInterior {
  readonly team: 0 | 1;
  readonly group = new THREE.Group();
  readonly entry = new THREE.Vector3();
  readonly queen = new THREE.Vector3();
  readonly exitMarker = new THREE.Vector3();
  readonly walkable: THREE.Mesh[] = [];
  readonly cameraView: { pos: THREE.Vector3; target: THREE.Vector3 };
  private readonly mixers: THREE.AnimationMixer[] = [];
  /** Player avatar inside the nest — a Group that starts as a placeholder
   *  sphere and swaps to a yellow-tinted skinned ant once the worker GLB loads. */
  readonly playerMarker: THREE.Group;
  private playerMarkerDot: THREE.Mesh | null = null;
  private playerMarkerAnt: THREE.Object3D | null = null;
  private playerMarkerMixer: THREE.AnimationMixer | null = null;
  private playerMarkerWalk: THREE.AnimationAction | null = null;
  private playerMarkerMoving = false;
  private readonly residents: Resident[] = [];
  /** Local graph positions + kinds. */
  private readonly nodes: NodeRec[] = [];
  private readonly adjacency: number[][] = [];
  /** Curve for each edge, keyed by "min(a,b):max(a,b)" — residents walk along
   *  these so they stay inside the tubes instead of cutting through rock. */
  private readonly edgeCurves = new Map<string, THREE.CatmullRomCurve3>();
  /** Walkable waypoints expose node positions for resident pathing. */
  private get waypoints(): THREE.Vector3[] { return this.nodes.map((n) => n.pos); }
  private hostile = false;
  queenHp = 60;
  queenDead = false;
  /** Index of the queen's chamber node — nurses shuttle eggs from here. */
  private queenNodeIdx = 0;
  /** Shared depth-indexed materials so chambers/tubes shift from mossy near
   *  the surface to deep-olive further down, evoking soil depth. */
  private readonly tubeMatsByDepth = new Map<number, THREE.MeshBasicMaterial>();
  private readonly chamberMatsByDepth = new Map<number, THREE.MeshBasicMaterial>();
  private readonly queenNurses: Array<{
    mesh: THREE.Object3D;
    eggMesh: THREE.Mesh | null;
    x: number; y: number; z: number;
    tx: number; ty: number; tz: number;
    mode: 'toQueen' | 'toDrop';
    speed: number;
    hp: number;
  }> = [];

  private colorForDepth(d: number, kind: 'tube' | 'chamber'): number {
    void kind; // tubes and chambers share colour so they read as one excavation
    const t = Math.min(1, d / 6);
    // Warm dark brown near the surface, cooler and slightly darker deep down.
    const top = { r: 0x2a, g: 0x1a, b: 0x0e };
    const bot = { r: 0x0e, g: 0x08, b: 0x05 };
    const r = Math.round(top.r * (1 - t) + bot.r * t);
    const g = Math.round(top.g * (1 - t) + bot.g * t);
    const b = Math.round(top.b * (1 - t) + bot.b * t);
    return (r << 16) | (g << 8) | b;
  }

  private getTubeMat(depth: number): THREE.MeshBasicMaterial {
    const d = Math.max(0, Math.min(6, Math.round(depth)));
    let mat = this.tubeMatsByDepth.get(d);
    if (!mat) {
      mat = new THREE.MeshBasicMaterial({
        color: this.colorForDepth(d, 'tube'),
        side: THREE.DoubleSide,
      });
      this.tubeMatsByDepth.set(d, mat);
    }
    return mat;
  }

  private getChamberMat(depth: number): THREE.MeshBasicMaterial {
    const d = Math.max(0, Math.min(6, Math.round(depth)));
    let mat = this.chamberMatsByDepth.get(d);
    if (!mat) {
      mat = new THREE.MeshBasicMaterial({
        color: this.colorForDepth(d, 'chamber'),
        side: THREE.DoubleSide,
      });
      this.chamberMatsByDepth.set(d, mat);
    }
    return mat;
  }

  constructor(nest: NestConfig, team: 0 | 1) {
    this.team = team;
    this.group.name = `nest-interior-${team}`;
    this.group.position.set(nest.x, 0, nest.z);

    // Seeded RNG so each nest's shape is stable across reloads but different
    // from the other team's.
    const rng = mulberry32(team === 0 ? 0x7be1 : 0xa3f5);

    // Floor material stays opaque for click-raycast solidity. Tubes and
    // chambers use per-depth translucent greens gradienting from mossy
    // surface-green down to deep-olive — looks like soil-depth varying.
    const floorMat = new THREE.MeshBasicMaterial({ color: 0x0a3416, side: THREE.FrontSide });

    // Ant-farm view: no surface mound. The dirt-brown scene background
    //     covers everything not carved out by tunnels/chambers.

    const ENTRY_Y = -0.3;
    const entryPos = new THREE.Vector3(0, ENTRY_Y, 0);
    this.addNode({ pos: entryPos, depth: 0, kind: 'entry', radius: 0.5 });
    this.addChamberMeshes(entryPos, 0.5, 0, floorMat);

    // No visible exit ring in the ant-farm view — the player exits by tapping
    // the sky strip on the minimap (handled in Minimap.ts).
    this.exitMarker.set(nest.x, ENTRY_Y + 0.02, nest.z);
    this.entry.set(nest.x + 0.3, ENTRY_Y + 0.01, nest.z);

    // ─── Trunk → hub. Real colonies have a long vertical trunk before the
    //     first split, so go deep with minimal lateral drift. In the ant-farm
    //     2-D layout, the whole graph lives on the XY plane (Z locked to 0)
    //     so the side-view camera reads every tunnel at once. ─────────
    const hubPos = new THREE.Vector3(
      (rng() - 0.5) * 0.4,
      ENTRY_Y - (3.5 + rng() * 1.0),
      0,
    );
    const hubIdx = this.addNode({ pos: hubPos, depth: 1, kind: 'hub', radius: 0.5 });
    this.addEdgeTube(0, hubIdx, 0.32, 1);
    this.addChamberMeshes(hubPos, 0.5, 1, floorMat);

    // ─── Recursive branches — longer, more branchy, real-colony-looking ──
    const MAX_DEPTH = 6;
    const queenCandidates: Array<{ idx: number; depth: number; dist: number }> = [];

    const expand = (parentIdx: number, parentDir: THREE.Vector3, depth: number): void => {
      if (depth >= MAX_DEPTH) return;
      const parent = this.nodes[parentIdx];
      // 2–4 children per node; deeper nodes fork less. Real casts show trunks
      // that fork into several sub-tunnels, each with its own terminal.
      const childCount = Math.max(1, Math.round(2 + (rng() - 0.4) * 2.5 - depth * 0.3));
      for (let i = 0; i < childCount; i++) {
        // 2-D fan: each child gets an angle measured from straight-down. i=0
        // leans left, last child leans right, with jitter. Range roughly
        // ±80° so branches spray sideways as well as downward, but never up.
        const angleFromDown = ((i + 0.5) / childCount - 0.5) * 2.6 + (rng() - 0.5) * 0.5;
        const length = 4 + rng() * 5; // longer stretches than before
        const dir = new THREE.Vector3(
          Math.sin(angleFromDown),
          -Math.cos(angleFromDown),
          0,
        ).normalize();
        // Blend modest amount with parent so branches still spray outward.
        dir.multiplyScalar(0.55).addScaledVector(parentDir, 0.45);
        dir.z = 0; // stay in the ant-farm plane
        dir.normalize();
        const childPos = parent.pos.clone().addScaledVector(dir, length);
        childPos.z = 0;
        const isFood = depth >= 2 && rng() < 0.4;
        const isLeaf = depth === MAX_DEPTH - 1 || rng() < 0.20;
        const kind: NodeRec['kind'] = isFood ? 'food' : (isLeaf ? 'leaf' : 'tunnel');
        const radius = kind === 'food' ? 0.65 + rng() * 0.4
                      : kind === 'leaf' ? 0.45 + rng() * 0.35
                      : 0.2;
        const childIdx = this.addNode({ pos: childPos, depth, kind, radius });
        const tubeR = Math.max(0.26, 0.36 - depth * 0.025);
        this.addEdgeTube(parentIdx, childIdx, tubeR, depth);
        if (kind !== 'tunnel') {
          this.addChamberMeshes(childPos, radius, depth, floorMat);
          if (kind === 'food') {
            this.addFoodPile(childPos.x, childPos.y + 0.01, childPos.z, 8 + Math.floor(rng() * 14));
          }
        }
        // Mid-tunnel alcove: 35% chance any non-trivial tube spawns a small
        // side chamber halfway along its curve. Gives the cast the alveolar
        // "bubbles along the line" look of a real colony.
        if (length > 3 && rng() < 0.35 && depth < MAX_DEPTH - 1) {
          // In-plane perpendicular to the branch direction, biased slightly
          // downward so alcoves lean away from the surface.
          const alcoveDir = new THREE.Vector3(-dir.y, dir.x, 0).normalize();
          if (alcoveDir.y > 0) alcoveDir.negate(); // never point back up
          const alcovePos = parent.pos.clone()
            .lerp(childPos, 0.45 + rng() * 0.15)
            .addScaledVector(alcoveDir, 0.9 + rng() * 0.7);
          alcovePos.z = 0;
          const alcoveR = 0.4 + rng() * 0.25;
          const alcoveIdx = this.addNode({ pos: alcovePos, depth: depth + 1, kind: rng() < 0.5 ? 'food' : 'leaf', radius: alcoveR });
          this.addEdgeTube(childIdx, alcoveIdx, 0.24, depth + 1);
          this.addChamberMeshes(alcovePos, alcoveR, depth + 1, floorMat);
          if (this.nodes[alcoveIdx].kind === 'food') {
            this.addFoodPile(alcovePos.x, alcovePos.y + 0.01, alcovePos.z, 6 + Math.floor(rng() * 8));
          }
          queenCandidates.push({ idx: alcoveIdx, depth: depth + 1, dist: alcovePos.length() });
        }
        queenCandidates.push({ idx: childIdx, depth, dist: childPos.length() });
        if (!isLeaf) expand(childIdx, dir, depth + 1);
      }
    };
    // parentDir kept 2-D so the plane invariant survives the blend inside expand().
    expand(hubIdx, new THREE.Vector3(0, -1, 0), 2);

    // ─── Queen chamber = the LOWEST node in the tree (deepest Y) ────
    let queenIdx = hubIdx;
    let lowestY = this.nodes[hubIdx].pos.y;
    for (let i = 0; i < this.nodes.length; i++) {
      const n = this.nodes[i];
      if (n.kind === 'entry' || n.kind === 'hub') continue; // skip surface + junction
      if (n.pos.y < lowestY) { lowestY = n.pos.y; queenIdx = i; }
    }
    // If the recursion was pathological and produced no valid candidates, fall
    // back to the deepest candidate-list entry.
    if (queenIdx === hubIdx && queenCandidates.length > 0) {
      queenCandidates.sort((a, b) => a.dist - b.dist); // keep variable referenced
      let best = queenCandidates[0].idx;
      let bestY = this.nodes[best].pos.y;
      for (const c of queenCandidates) {
        if (this.nodes[c.idx].pos.y < bestY) { bestY = this.nodes[c.idx].pos.y; best = c.idx; }
      }
      queenIdx = best;
    }
    this.queenNodeIdx = queenIdx;
    const queenNode = this.nodes[queenIdx];
    // Widen her chamber so she reads as the boss room.
    queenNode.kind = 'queen';
    queenNode.radius = Math.max(queenNode.radius, 2.4);
    this.addChamberMeshes(queenNode.pos, queenNode.radius, queenNode.depth, floorMat);
    this.queen.set(nest.x + queenNode.pos.x, queenNode.pos.y + 0.05, nest.z + queenNode.pos.z);
    const queenLocal = queenNode.pos.clone();

    // ─── Walkable meshes = every chamber floor disc ─────────────
    //   addChamberMeshes already pushes to `this.walkable`, so there's nothing
    //   more to do for pathing; resident BFS uses this.nodes + adjacency.

    // ─── Lighting: embered warm glows at every chamber + global hemi ──
    for (const n of this.nodes) {
      if (n.kind === 'entry') this.group.add(this.pointLight(n.pos.x, n.pos.y + 0.8, n.pos.z, 0xffcc88, 1.2, 5));
      else if (n.kind === 'hub') this.group.add(this.pointLight(n.pos.x, n.pos.y + 1.2, n.pos.z, 0xffaa70, 1.4, 9));
      else if (n.kind === 'food') this.group.add(this.pointLight(n.pos.x, n.pos.y + 0.6, n.pos.z, 0xfff0a0, 1.0, 6));
      else if (n.kind === 'queen') this.group.add(this.pointLight(n.pos.x, n.pos.y + 1.2, n.pos.z, 0xfff0c0, 2.2, 14));
    }
    this.group.add(new THREE.HemisphereLight(0x0a4018, 0x021008, 0.4));

    // ─── Player marker — Group containing a placeholder dot that gets swapped
    //     for a yellow worker-ant skinned clone as soon as the GLB loads. ─
    this.playerMarker = new THREE.Group();
    this.playerMarker.name = 'player-marker';
    this.playerMarker.position.set(0, ENTRY_Y - 0.05, ANT_Z_LIFT);
    const dotGeo = new THREE.SphereGeometry(0.09, 14, 10);
    const dotMat = new THREE.MeshStandardMaterial({
      color: 0xffe04a, emissive: 0x3a2600, emissiveIntensity: 0.8, roughness: 0.3,
    });
    this.playerMarkerDot = new THREE.Mesh(dotGeo, dotMat);
    this.playerMarker.add(this.playerMarkerDot);
    this.group.add(this.playerMarker);

    // ─── Camera: locked side view straight along -Z. The whole graph lives on
    //     the XY plane; we frame it wide enough to see mound → queen chamber
    //     with a bit of margin on each side. ─────────────────────────
    const deepestY = Math.min(...this.nodes.map((n) => n.pos.y));
    const midY = (ENTRY_Y + deepestY) * 0.5;
    const spanX = Math.max(
      ...this.nodes.map((n) => Math.abs(n.pos.x)),
    );

    // No dirt background plane needed — NestMode paints scene.background as
    // dirt brown while underground, so any pixel not carved by a tunnel or
    // chamber reads as soil.


    // Frame the whole excavation with a small margin. The perspective camera
    // uses FOV = 55° (vertical), so distance = halfSpan / tan(halfFov).
    const HALF_V_FOV = (55 * Math.PI) / 360;
    const HALF_H_FOV = Math.atan(Math.tan(HALF_V_FOV) * (16 / 9));
    const vSpan = Math.abs(ENTRY_Y - deepestY);
    const hSpan = Math.max(spanX * 2, 6);
    const camR = Math.max(
      (vSpan * 0.6) / Math.tan(HALF_V_FOV),
      (hSpan * 0.6) / Math.tan(HALF_H_FOV),
      8,
    );
    this.cameraView = {
      pos: new THREE.Vector3(nest.x, midY, nest.z + camR),
      target: new THREE.Vector3(nest.x, midY, nest.z),
    };

    this.group.visible = false;
    this.allocateResidentSlots(14);
    void this.loadQueenAndAttendants(queenLocal);
    void this.loadResidentBodies();
    void this.loadPlayerMarkerAnt();
  }

  // ─── Geometry primitives ────────────────────────────────────────

  private addNode(n: NodeRec): number {
    this.nodes.push(n);
    this.adjacency.push([]);
    return this.nodes.length - 1;
  }

  private addEdge(a: number, b: number): void {
    this.adjacency[a].push(b);
    this.adjacency[b].push(a);
  }

  private addChamberMeshes(pos: THREE.Vector3, radius: number, depthOrMat: number | THREE.Material, floor: THREE.Material): void {
    void floor; // ant-farm view has no lit floor mesh; click-target below
    const blob = typeof depthOrMat === 'number' ? this.getChamberMat(depthOrMat) : depthOrMat;

    // Ant-farm chamber: a wide, squat, hand-drawn oval on the dirt background.
    // Build a triangle fan out of a jittered ellipse so each chamber looks
    // organic. The chamber and the tunnel materials are the same colour so
    // they blend into one continuous excavation.
    const SIDES = 28;
    const positions = new Float32Array((SIDES + 2) * 3);
    positions[0] = 0; positions[1] = 0; positions[2] = 0;
    for (let i = 0; i <= SIDES; i++) {
      const a = (i / SIDES) * Math.PI * 2;
      // Squat: wider than tall, like a real chamber cross-section.
      const rx = radius * (1.25 + (Math.random() - 0.5) * 0.16);
      const ry = radius * (0.7 + (Math.random() - 0.5) * 0.14);
      positions[(i + 1) * 3 + 0] = Math.cos(a) * rx;
      positions[(i + 1) * 3 + 1] = Math.sin(a) * ry;
      positions[(i + 1) * 3 + 2] = 0;
    }
    const indices: number[] = [];
    for (let i = 1; i <= SIDES; i++) {
      indices.push(0, i, i + 1);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setIndex(indices);
    const mesh = new THREE.Mesh(geo, blob);
    mesh.position.copy(pos);
    this.group.add(mesh);

    // Invisible click-target disc — raycaster still hits meshes with
    // visible=false, so any click inside the chamber routes to the node.
    const clickGeo = new THREE.CircleGeometry(radius * 1.3, 20);
    const click = new THREE.Mesh(
      clickGeo,
      new THREE.MeshBasicMaterial({ visible: false }),
    );
    click.position.copy(pos);
    click.visible = false;
    this.group.add(click);
    this.walkable.push(click);
  }

  /** Tunnel mesh between two nodes + the CatmullRom curve is returned so
   *  residents can walk along it (they must stay inside their own tubes). */
  private addTube(a: THREE.Vector3, b: THREE.Vector3, radius: number, mat: THREE.Material): THREE.CatmullRomCurve3 {
    const dir = new THREE.Vector3().subVectors(b, a);
    const len = dir.length();
    dir.normalize();
    const perp = new THREE.Vector3(-dir.y, dir.x, 0);
    if (perp.lengthSq() < 1e-6) perp.set(1, 0, 0);
    perp.normalize();
    const steps = 5 + Math.floor(Math.random() * 2);
    const points: THREE.Vector3[] = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const base = new THREE.Vector3().lerpVectors(a, b, t);
      if (i !== 0 && i !== steps) {
        const lat = (Math.random() - 0.5) * len * 0.18;
        base.addScaledVector(perp, lat);
      }
      base.z = 0;
      points.push(base);
    }
    const curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.3);

    // Ant-farm view: build a FLAT 2-D ribbon along the curve instead of a
    // TubeGeometry cylinder. Sample the curve, take the in-plane perpendicular
    // at each sample, extrude ±radius to form a strip of triangles all at Z=0.
    // The result reads as a solid tunnel pocket cut into the dirt background.
    const SEGMENTS = Math.max(28, steps * 6);
    const positions = new Float32Array((SEGMENTS + 1) * 2 * 3);
    const indices: number[] = [];
    const p = new THREE.Vector3();
    const pAhead = new THREE.Vector3();
    const tangent = new THREE.Vector3();
    const rib = new THREE.Vector3();
    for (let i = 0; i <= SEGMENTS; i++) {
      const t = i / SEGMENTS;
      curve.getPointAt(t, p);
      const tNext = i === SEGMENTS ? Math.max(0, t - 1 / SEGMENTS) : Math.min(1, t + 1 / SEGMENTS);
      curve.getPointAt(tNext, pAhead);
      tangent.subVectors(pAhead, p);
      if (i === SEGMENTS) tangent.negate();
      if (tangent.lengthSq() < 1e-9) tangent.set(1, 0, 0);
      tangent.normalize();
      rib.set(-tangent.y, tangent.x, 0).normalize();
      const base = i * 6;
      positions[base + 0] = p.x + rib.x * radius;
      positions[base + 1] = p.y + rib.y * radius;
      positions[base + 2] = 0;
      positions[base + 3] = p.x - rib.x * radius;
      positions[base + 4] = p.y - rib.y * radius;
      positions[base + 5] = 0;
    }
    for (let i = 0; i < SEGMENTS; i++) {
      const v = i * 2;
      indices.push(v, v + 1, v + 2);
      indices.push(v + 2, v + 1, v + 3);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setIndex(indices);
    const m = new THREE.Mesh(geo, mat);
    this.group.add(m);
    return curve;
  }

  /** Convenience wrapper: run addTube and register the returned curve against
   *  edge (a↔b) so the path walker can retrieve it. */
  private addEdgeTube(aIdx: number, bIdx: number, radius: number, depthOrMat: number | THREE.Material): void {
    const mat = typeof depthOrMat === 'number' ? this.getTubeMat(depthOrMat) : depthOrMat;
    const curve = this.addTube(this.nodes[aIdx].pos, this.nodes[bIdx].pos, radius, mat);
    this.addEdge(aIdx, bIdx);
    const key = aIdx < bIdx ? `${aIdx}:${bIdx}` : `${bIdx}:${aIdx}`;
    this.edgeCurves.set(key, curve);
  }

  getEdgeCurve(from: number, to: number): { curve: THREE.CatmullRomCurve3; forward: boolean } | null {
    const key = from < to ? `${from}:${to}` : `${to}:${from}`;
    const curve = this.edgeCurves.get(key);
    if (!curve) return null;
    return { curve, forward: from < to };
  }

  /** For NestMode's player-ant pathing. Returns the node index closest to a
   *  point given in LOCAL space. Uses 3-D distance with a vertical weight so
   *  that stacked chambers (entry + hub + queen all at x=z=0) don't collapse
   *  into "whichever is first". */
  nearestNodeLocal(x: number, z: number, y = 0): number {
    let bestI = 0;
    let bestD2 = Infinity;
    for (let i = 0; i < this.nodes.length; i++) {
      const n = this.nodes[i].pos;
      const dx = n.x - x;
      const dz = n.z - z;
      const dy = n.y - y;
      // Weight Y × 2 so clicks on a deep chamber beat surface chambers that
      // happen to share XZ; still planar enough that lateral clicks prefer
      // the actual lateral chamber.
      const d2 = dx * dx + dz * dz + dy * dy * 2;
      if (d2 < bestD2) { bestD2 = d2; bestI = i; }
    }
    return bestI;
  }

  nodeLocalPos(i: number, out = new THREE.Vector3()): THREE.Vector3 { return out.copy(this.nodes[i].pos); }
  path(from: number, to: number): number[] { return this.bfsPath(from, to); }
  edgeLength(from: number, to: number): number { return this.edgeLenFor([from, to], 1); }
  setPlayerMarkerLocal(x: number, y: number, z: number, yaw?: number, tangent?: THREE.Vector3): void {
    void z; // ant-farm nest: nodes/curves live on Z=0; marker rides at ANT_Z_LIFT
    this.playerMarker.position.set(x, y, ANT_Z_LIFT);
    if (tangent && tangent.lengthSq() > 1e-9) {
      const t = tangent.clone().normalize();
      // +Z is the worker-ant GLB's default forward axis.
      const forward = new THREE.Vector3(0, 0, 1);
      this.playerMarker.quaternion.setFromUnitVectors(forward, t);
    } else if (yaw !== undefined) {
      this.playerMarker.rotation.set(0, yaw, 0);
    }
  }

  /** Called by NestMode every tick — drives the walk animation timescale. */
  setPlayerMarkerMoving(moving: boolean): void {
    this.playerMarkerMoving = moving;
    if (this.playerMarkerWalk) this.playerMarkerWalk.timeScale = moving ? 1.3 : 0;
  }

  private pointLight(x: number, y: number, z: number, color: number, intensity: number, distance: number): THREE.PointLight {
    const l = new THREE.PointLight(color, intensity, distance, 2);
    l.position.set(x, y, z);
    return l;
  }

  private addFoodPile(x: number, y: number, z: number, count: number): void {
    const geo = new THREE.SphereGeometry(0.025, 10, 8);
    // Warm amber, not cream — no whites in this underground world.
    const mat = new THREE.MeshStandardMaterial({
      color: 0xff9030, emissive: 0x3a1804, emissiveIntensity: 0.4, roughness: 0.4,
    });
    for (let i = 0; i < count; i++) {
      const m = new THREE.Mesh(geo, mat);
      const r = 0.4 * Math.sqrt(Math.random());
      const a = Math.random() * Math.PI * 2;
      m.position.set(x + Math.cos(a) * r, y + Math.random() * 0.04, z + Math.sin(a) * r);
      m.castShadow = true;
      m.receiveShadow = true;
      this.group.add(m);
    }
  }

  // ─── BFS pathing ────────────────────────────────────────────────

  private bfsPath(start: number, end: number): number[] {
    if (start === end) return [start];
    const prev = new Array<number>(this.nodes.length).fill(-1);
    const visited = new Array<boolean>(this.nodes.length).fill(false);
    const q: number[] = [start];
    visited[start] = true;
    while (q.length) {
      const u = q.shift()!;
      if (u === end) break;
      for (const v of this.adjacency[u]) {
        if (visited[v]) continue;
        visited[v] = true;
        prev[v] = u;
        q.push(v);
      }
    }
    if (prev[end] === -1 && end !== start) return [start];
    const out: number[] = [];
    for (let c = end; c !== -1; c = prev[c]) { out.unshift(c); if (c === start) break; }
    return out;
  }

  // ─── Residents ──────────────────────────────────────────────────

  private allocateResidentSlots(count: number): void {
    const geo = new THREE.SphereGeometry(0.02, 6, 4);
    const mat = new THREE.MeshStandardMaterial({ color: 0x000000 });
    // First 3 of the count are carriers: they start at the queen node and
    // shuttle eggs out to the other chambers.
    const CARRIERS = Math.min(3, count);
    for (let i = 0; i < count; i++) {
      const isCarrier = i < CARRIERS;
      const startNode = isCarrier ? this.queenNodeIdx : i % this.nodes.length;
      const endNode = isCarrier ? this.pickDepositChamber() : this.pickDistinctDestination(startNode);
      const path = this.bfsPath(startNode, endNode);
      const wp = this.waypoints[startNode];
      const placeholder = new THREE.Mesh(geo, mat);
      placeholder.visible = false;
      this.group.add(placeholder);
      this.residents.push({
        x: wp.x, z: wp.z, y: wp.y,
        path,
        pathIdx: Math.min(1, path.length - 1),
        t: 0,
        edgeLength: this.edgeLenFor(path, Math.min(1, path.length - 1)),
        speed: isCarrier ? 0.9 + Math.random() * 0.2 : 0.7 + Math.random() * 0.3,
        hp: 3,
        kind: 'worker',
        role: isCarrier ? 'carrier' : 'wander',
        carrierState: isCarrier ? 'toDeposit' : 'toQueen',
        eggMesh: null,
        mesh: placeholder,
        hitPulseT: 0,
        retargetT: 0,
      });
    }
  }

  /** Pick any non-queen, non-entry chamber node to drop an egg in. */
  private pickDepositChamber(): number {
    const candidates: number[] = [];
    for (let i = 0; i < this.nodes.length; i++) {
      const n = this.nodes[i];
      if (n.kind === 'queen' || n.kind === 'entry' || n.kind === 'hub') continue;
      if (n.kind === 'tunnel') continue;
      candidates.push(i);
    }
    if (candidates.length === 0) {
      // No chambers — fall back to any non-queen node.
      for (let i = 0; i < this.nodes.length; i++) if (i !== this.queenNodeIdx) candidates.push(i);
    }
    return candidates[Math.floor(Math.random() * candidates.length)];
  }

  /** Drop a visible egg at a chamber node — a small creamy oval that
   *  accumulates as the nurses bring more. */
  private depositEgg(nodeIdx: number): void {
    const n = this.nodes[nodeIdx];
    const geo = new THREE.SphereGeometry(0.04, 10, 8);
    const mat = new THREE.MeshStandardMaterial({
      color: 0xfff2c0, emissive: 0x2a2010, emissiveIntensity: 0.35, roughness: 0.5,
    });
    const egg = new THREE.Mesh(geo, mat);
    // Scatter around the chamber floor a little.
    const ang = Math.random() * Math.PI * 2;
    const r = Math.random() * Math.max(0.08, n.radius * 0.6);
    egg.position.set(n.pos.x + Math.cos(ang) * r, n.pos.y + 0.02, n.pos.z + Math.sin(ang) * r);
    egg.scale.set(1, 0.75, 1);
    this.group.add(egg);
  }

  private pickDistinctDestination(from: number): number {
    if (this.nodes.length <= 1) return from;
    let d = Math.floor(Math.random() * this.nodes.length);
    if (d === from) d = (d + 1) % this.nodes.length;
    return d;
  }

  private edgeLenFor(path: number[], pathIdx: number): number {
    if (pathIdx <= 0 || pathIdx >= path.length) return 1;
    const from = path[pathIdx - 1];
    const to = path[pathIdx];
    const eg = this.getEdgeCurve(from, to);
    if (!eg) return this.nodes[from].pos.distanceTo(this.nodes[to].pos);
    return eg.curve.getLength();
  }

  private async loadResidentBodies(): Promise<void> {
    try {
      const gltf = await new GLTFLoader().loadAsync(WORKER_URL);
      const src = gltf.scene;
      src.updateMatrixWorld(true);
      const bb = new THREE.Box3().setFromObject(src);
      const sz = new THREE.Vector3(); bb.getSize(sz);
      const longest = Math.max(sz.x, sz.y, sz.z) || 1;
      const residentBody = 0.18;
      const normalize = residentBody / longest;
      const clip = gltf.animations.find((c) => /walk/i.test(c.name)) ?? gltf.animations[0];
      const baseColor = this.team === 0 ? 0xc23025 : 0x141414;

      for (const r of this.residents) {
        const cloned = skeletonClone(src);
        cloned.scale.setScalar(normalize);
        const cbb = new THREE.Box3().setFromObject(cloned);
        const cs = new THREE.Vector3(); cbb.getSize(cs);
        cloned.position.set(r.x, r.y + cs.y * 0.5 - 0.04, r.z);
        cloned.traverse((o) => {
          const m = o as THREE.Mesh;
          if (!m.isMesh) return;
          m.castShadow = true;
          m.receiveShadow = true;
          m.frustumCulled = false;
          const mat = (m as THREE.Mesh & { material?: THREE.Material }).material;
          if (mat && (mat as THREE.MeshStandardMaterial).color) {
            const mm = (mat as THREE.MeshStandardMaterial).clone();
            mm.color = new THREE.Color(baseColor);
            mm.emissive = new THREE.Color(this.team === 0 ? 0x2a0806 : 0x050505);
            m.material = mm;
          }
        });
        r.mesh.visible = false;
        r.mesh = cloned as unknown as THREE.Mesh;
        this.group.add(cloned);
        if (clip) {
          const mixer = new THREE.AnimationMixer(cloned);
          const act = mixer.clipAction(clip);
          act.timeScale = 1.2 + Math.random() * 0.4;
          act.play();
          this.mixers.push(mixer);
        }
        // Carriers get a tiny creamy egg attached to their mandibles; we
        // toggle its visibility between the queen and the deposit chamber.
        if (r.role === 'carrier') {
          const eggGeo = new THREE.SphereGeometry(0.05, 10, 8);
          const eggMat = new THREE.MeshStandardMaterial({
            color: 0xfff2c0, emissive: 0x2a2010, emissiveIntensity: 0.4, roughness: 0.5,
          });
          const egg = new THREE.Mesh(eggGeo, eggMat);
          egg.scale.set(1, 0.75, 1);
          // Place in front of head — resident body is ~0.18 m with head at +X local.
          egg.position.set(0.11, 0.05, 0);
          egg.castShadow = true;
          egg.visible = r.carrierState === 'toDeposit';
          cloned.add(egg);
          r.eggMesh = egg;
        }
      }
    } catch (err) {
      console.warn('[NestInterior] resident GLB failed, keeping placeholders:', err);
    }
  }

  /** Load a yellow-tinted worker-ant clone into `playerMarker`, replacing the
   *  placeholder sphere. Keeps the marker facing travel direction via the
   *  NestMode.update path-walker which calls setPlayerMarkerLocal(x, y, z, yaw). */
  private async loadPlayerMarkerAnt(): Promise<void> {
    try {
      const gltf = await new GLTFLoader().loadAsync(WORKER_URL);
      const src = gltf.scene;
      src.updateMatrixWorld(true);
      const bb = new THREE.Box3().setFromObject(src);
      const sz = new THREE.Vector3(); bb.getSize(sz);
      const longest = Math.max(sz.x, sz.y, sz.z) || 1;
      const bodyLen = 0.22; // a touch bigger than residents
      const normalize = bodyLen / longest;
      const clip = gltf.animations.find((c) => /walk/i.test(c.name)) ?? gltf.animations[0];
      const cloned = skeletonClone(src);
      cloned.scale.setScalar(normalize);
      // Centre feet on Y=0, head forward (+X), so rotation.y = yaw works.
      cloned.updateMatrixWorld(true);
      const cbb = new THREE.Box3().setFromObject(cloned);
      const c = new THREE.Vector3(); cbb.getCenter(c);
      cloned.position.set(-c.x, -cbb.min.y, -c.z);
      cloned.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.castShadow = true;
        m.receiveShadow = true;
        m.frustumCulled = false;
        const mat = (m as THREE.Mesh & { material?: THREE.Material }).material;
        if (mat && (mat as THREE.MeshStandardMaterial).color) {
          const mm = (mat as THREE.MeshStandardMaterial).clone();
          mm.color = new THREE.Color(0xffd23a);
          mm.emissive = new THREE.Color(0x5a3a00);
          m.material = mm;
        }
      });
      this.playerMarker.add(cloned);
      this.playerMarkerAnt = cloned;
      // Hide the placeholder dot now that the real ant is in place.
      if (this.playerMarkerDot) this.playerMarkerDot.visible = false;
      if (clip) {
        this.playerMarkerMixer = new THREE.AnimationMixer(cloned);
        this.playerMarkerWalk = this.playerMarkerMixer.clipAction(clip);
        this.playerMarkerWalk.play();
        this.playerMarkerWalk.timeScale = 0;
        this.mixers.push(this.playerMarkerMixer);
      }
      void this.playerMarkerMoving;
      void this.playerMarkerAnt;
    } catch (err) {
      console.warn('[NestInterior] yellow ant marker load failed:', err);
    }
  }

  /** During a recall, funnel residents down: soldiers (the first quarter of
   *  the list by id) hold the entry chamber, everyone else packs into the
   *  queen chamber. */
  private recalled = false;
  private readonly entryGuardSet = new Set<number>();
  setRecalled(on: boolean): void {
    if (this.recalled === on) return;
    this.recalled = on;
    if (on) {
      // Pick ~1/4 of the residents to park at the entry chamber as guards.
      this.entryGuardSet.clear();
      const guardCount = Math.max(1, Math.floor(this.residents.length * 0.25));
      for (let i = 0; i < guardCount; i++) this.entryGuardSet.add(i);
      for (let i = 0; i < this.residents.length; i++) {
        const r = this.residents[i];
        const currentNode = r.pathIdx === 0 ? r.path[0] : r.path[r.pathIdx - 1];
        const target = this.entryGuardSet.has(i) ? 0 /* entry node */ : this.queenNodeIdx;
        r.path = this.bfsPath(currentNode, target);
        r.pathIdx = Math.min(1, r.path.length - 1);
        r.t = 0;
        r.edgeLength = this.edgeLenFor(r.path, r.pathIdx);
        r.speed = 1.3 + Math.random() * 0.3;
      }
    } else {
      this.entryGuardSet.clear();
    }
  }

  /** Player followers that descended with her — spawned on entry, cleared on
   *  ascent. They path-walk toward the player's current node every frame. */
  private readonly followers: Array<{
    mesh: THREE.Object3D;
    x: number; y: number; z: number;
    currentNode: number;
    path: number[];
    pathIdx: number;
    t: number;
    edgeLength: number;
    retargetT: number;
  }> = [];

  async spawnFollowers(count: number): Promise<void> {
    if (count <= 0) return;
    const entryLocal = new THREE.Vector3(0, -0.3, 0);
    try {
      const gltf = await new GLTFLoader().loadAsync(WORKER_URL);
      const src = gltf.scene;
      src.updateMatrixWorld(true);
      const bb = new THREE.Box3().setFromObject(src);
      const sz = new THREE.Vector3(); bb.getSize(sz);
      const longest = Math.max(sz.x, sz.y, sz.z) || 1;
      const bodyLen = 0.18;
      const normalize = bodyLen / longest;
      const clip = gltf.animations.find((c) => /walk/i.test(c.name)) ?? gltf.animations[0];
      for (let i = 0; i < count; i++) {
        const cloned = skeletonClone(src);
        cloned.scale.setScalar(normalize);
        const cbb = new THREE.Box3().setFromObject(cloned);
        const c = new THREE.Vector3(); cbb.getCenter(c);
        cloned.position.set(entryLocal.x - c.x, entryLocal.y - cbb.min.y, entryLocal.z - c.z);
        cloned.traverse((o) => {
          const m = o as THREE.Mesh;
          if (!m.isMesh) return;
          m.castShadow = true;
          m.receiveShadow = true;
          m.frustumCulled = false;
          const mat = (m as THREE.Mesh & { material?: THREE.Material }).material;
          if (mat && (mat as THREE.MeshStandardMaterial).color) {
            const mm = (mat as THREE.MeshStandardMaterial).clone();
            mm.color = new THREE.Color(0x4dd2ff); // cyan follower tint
            mm.emissive = new THREE.Color(0x0a2a3a);
            m.material = mm;
          }
        });
        this.group.add(cloned);
        if (clip) {
          const mixer = new THREE.AnimationMixer(cloned);
          const act = mixer.clipAction(clip);
          act.timeScale = 1.2 + Math.random() * 0.3;
          act.play();
          this.mixers.push(mixer);
        }
        const nodeIdx = 0; // entry node
        this.followers.push({
          mesh: cloned,
          x: entryLocal.x, y: entryLocal.y, z: entryLocal.z,
          currentNode: nodeIdx,
          path: [nodeIdx],
          pathIdx: 0,
          t: 0,
          edgeLength: 1,
          retargetT: 0,
        });
      }
    } catch (err) {
      console.warn('[NestInterior] follower spawn failed:', err);
    }
  }

  clearFollowers(): void {
    for (const f of this.followers) {
      this.group.remove(f.mesh);
    }
    this.followers.length = 0;
  }

  /** Path each follower toward the node nearest the player marker. Called
   *  from update() so followers shadow the yellow ant down the tunnels. */
  private tickFollowers(dt: number, playerLocalX: number, playerLocalZ: number): void {
    if (this.followers.length === 0) return;
    const pointA = new THREE.Vector3();
    const pointB = new THREE.Vector3();
    const MODEL_FORWARD = new THREE.Vector3(0, 0, 1);
    const tangent = new THREE.Vector3();
    const targetNode = this.nearestNodeLocal(playerLocalX, playerLocalZ, this.playerMarker.position.y);
    for (const f of this.followers) {
      f.retargetT -= dt;
      if (f.retargetT <= 0) {
        f.path = this.bfsPath(f.currentNode, targetNode);
        f.pathIdx = Math.min(1, f.path.length - 1);
        f.t = 0;
        f.edgeLength = this.edgeLenFor(f.path, f.pathIdx);
        f.retargetT = 0.4;
      }
      if (f.pathIdx <= 0 || f.pathIdx >= f.path.length) continue;
      const from = f.path[f.pathIdx - 1];
      const to = f.path[f.pathIdx];
      const edge = this.getEdgeCurve(from, to);
      const speed = 1.8;
      f.t += (speed * dt) / Math.max(0.1, f.edgeLength);
      if (f.t >= 1) {
        f.t = 0;
        f.currentNode = to;
        f.pathIdx++;
        if (f.pathIdx >= f.path.length) continue;
        f.edgeLength = this.edgeLenFor(f.path, f.pathIdx);
      }
      if (!edge) continue;
      const tEval = edge.forward ? f.t : (1 - f.t);
      edge.curve.getPointAt(Math.max(0, Math.min(1, tEval)), pointA);
      const tAhead = edge.forward ? Math.min(1, tEval + 0.02) : Math.max(0, tEval - 0.02);
      edge.curve.getPointAt(tAhead, pointB);
      f.x = pointA.x;
      f.y = pointA.y + 0.02;
      f.z = pointA.z;
      tangent.subVectors(pointB, pointA);
      f.mesh.position.set(f.x, f.y, ANT_Z_LIFT);
      if (tangent.lengthSq() > 1e-9) {
        tangent.normalize();
        (f.mesh as THREE.Object3D & { quaternion: THREE.Quaternion }).quaternion.setFromUnitVectors(MODEL_FORWARD, tangent);
      }

      // In an enemy nest, followers chew through nurses + the queen when in
      // bite range. Friendly nest: pure escort, no damage.
      if (this.hostile) {
        const qNode = this.nodes[this.queenNodeIdx].pos;
        const qdx = qNode.x - f.x;
        const qdz = qNode.z - f.z;
        if (qdx * qdx + qdz * qdz < 0.64) this.damageQueen(6 * dt);
        for (const n of this.queenNurses) {
          if (n.hp <= 0) continue;
          const ndx = n.x - f.x;
          const ndz = n.z - f.z;
          if (ndx * ndx + ndz * ndz < 0.16) {
            n.hp -= 6 * dt;
            if (n.hp <= 0) {
              n.mesh.visible = false;
              if (n.eggMesh) n.eggMesh.visible = false;
            }
          }
        }
      }
    }
  }

  setHostility(on: boolean): void {
    if (this.hostile === on) return;
    this.hostile = on;
    for (const r of this.residents) {
      r.kind = on ? 'guard' : 'worker';
      r.speed = on ? 1.8 + Math.random() * 0.5 : 0.7 + Math.random() * 0.3;
      r.mesh.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        const mat = m.material as THREE.MeshStandardMaterial;
        if (mat && mat.color) {
          mat.color.setHex(on ? 0xff3a20 : (this.team === 0 ? 0xc23025 : 0x141414));
          mat.emissive.setHex(on ? 0x501008 : (this.team === 0 ? 0x2a0806 : 0x050505));
        }
      });
    }
  }

  setPlayerMarkerWorld(wx: number, wz: number): void {
    void wz; // Z is locked to the nest plane in 2-D mode; only X moves.
    const localX = wx - this.group.position.x;
    // Ant-farm nest: floor discs are edge-on to the camera and share Y with
    // their chamber node, so pick nearest node in the XY plane and snap Y to
    // its centre. Z always stays at PLAYER_Z_LIFT so the ant renders in front.
    let bestY = -0.5;
    let bestD2 = Infinity;
    for (const n of this.nodes) {
      const dx = localX - n.pos.x;
      const d2 = dx * dx;
      if (d2 < bestD2) { bestD2 = d2; bestY = n.pos.y; }
    }
    this.playerMarker.position.set(localX, bestY, ANT_Z_LIFT);
  }

  private async loadQueenAndAttendants(queenLocal: THREE.Vector3): Promise<void> {
    const loader = new GLTFLoader();
    try {
      const gltf = await loader.loadAsync(QUEEN_URL);
      const tune = readTune(QUEEN_TUNE_ID, QUEEN_TUNE_DEFAULTS);
      const scene = gltf.scene;
      scene.rotation.set(tune.rotX, tune.rotY, tune.rotZ);
      scene.scale.setScalar(tune.scale * 3.5);
      scene.updateMatrixWorld(true);
      const bb = new THREE.Box3().setFromObject(scene);
      const c = new THREE.Vector3();
      bb.getCenter(c);
      scene.position.set(queenLocal.x - c.x, queenLocal.y - bb.min.y + tune.offY, queenLocal.z - c.z);
      scene.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.castShadow = true;
        m.receiveShadow = true;
        m.frustumCulled = false;
      });
      this.group.add(scene);
    } catch (err) {
      console.warn('[NestInterior] queen GLB failed:', err);
    }
    try {
      const gltf = await loader.loadAsync(WORKER_URL);
      const src = gltf.scene;
      src.updateMatrixWorld(true);
      const bb = new THREE.Box3().setFromObject(src);
      const sz = new THREE.Vector3(); bb.getSize(sz);
      const longest = Math.max(sz.x, sz.y, sz.z) || 1;
      const nurseLength = 0.5;
      const normalize = nurseLength / longest;
      const clip = gltf.animations.find((c) => /walk/i.test(c.name)) ?? gltf.animations[0];
      const queenRadius = this.nodes[this.queenNodeIdx].radius;
      for (let i = 0; i < 4; i++) {
        const cloned = skeletonClone(src);
        cloned.scale.setScalar(normalize);
        const cbb = new THREE.Box3().setFromObject(cloned);
        const cs = new THREE.Vector3(); cbb.getSize(cs);
        const startAng = (i / 4) * Math.PI * 2 + 0.25;
        const nx = queenLocal.x + Math.cos(startAng) * 1.2;
        const nz = queenLocal.z + Math.sin(startAng) * 1.2;
        cloned.position.set(nx, queenLocal.y + cs.y * 0.5, nz);
        cloned.traverse((o) => {
          const m = o as THREE.Mesh;
          if (!m.isMesh) return;
          m.castShadow = true;
          m.receiveShadow = true;
          m.frustumCulled = false;
          const mat = (m as THREE.Mesh & { material?: THREE.Material }).material;
          if (mat && (mat as THREE.MeshStandardMaterial).color) {
            const mm = (mat as THREE.MeshStandardMaterial).clone();
            mm.color = new THREE.Color(0x8c5a30);
            mm.emissive = new THREE.Color(0x2a2418);
            m.material = mm;
          }
        });
        if (clip) {
          const mixer = new THREE.AnimationMixer(cloned);
          const act = mixer.clipAction(clip);
          act.timeScale = 1.1 + Math.random() * 0.3;
          act.play();
          this.mixers.push(mixer);
        }
        // Egg in the nurse's mandibles — toggled by the egg-shuttle loop.
        const eggGeo = new THREE.SphereGeometry(0.1, 10, 8);
        const eggMat = new THREE.MeshStandardMaterial({
          color: 0xfff2c0, emissive: 0x2a2010, emissiveIntensity: 0.4, roughness: 0.5,
        });
        const egg = new THREE.Mesh(eggGeo, eggMat);
        egg.scale.set(1, 0.75, 1);
        egg.position.set(0.22, 0.1, 0);  // ahead of head in local space
        egg.visible = false;
        cloned.add(egg);
        this.group.add(cloned);
        // Seed: start carrying from queen on first tick.
        const firstDropAng = Math.random() * Math.PI * 2;
        const firstDropR = (0.5 + Math.random() * 0.5) * queenRadius;
        this.queenNurses.push({
          mesh: cloned,
          eggMesh: egg,
          x: nx, y: queenLocal.y, z: nz,
          tx: queenLocal.x + Math.cos(firstDropAng) * firstDropR,
          ty: queenLocal.y,
          tz: queenLocal.z + Math.sin(firstDropAng) * firstDropR,
          mode: 'toDrop',
          speed: 0.55 + Math.random() * 0.15,
          hp: 4,
        });
      }
    } catch (err) {
      console.warn('[NestInterior] worker GLB failed for nurses:', err);
    }
  }

  /** Place a small egg at a specific local point (used by the in-chamber
   *  nurses, not the tunnel-shuttling carriers). */
  private addEggAt(lx: number, ly: number, lz: number): void {
    const geo = new THREE.SphereGeometry(0.04, 10, 8);
    const mat = new THREE.MeshStandardMaterial({
      color: 0xfff2c0, emissive: 0x2a2010, emissiveIntensity: 0.4, roughness: 0.5,
    });
    const egg = new THREE.Mesh(geo, mat);
    egg.position.set(lx, ly + 0.02, lz);
    egg.scale.set(1, 0.75, 1);
    egg.castShadow = true;
    this.group.add(egg);
  }

  update(dt: number, playerLocalX: number, playerLocalZ: number, playerLocalY: number): number {
    void playerLocalY;
    for (const m of this.mixers) m.update(dt);
    let damageToPlayer = 0;
    const BITE_R2 = 0.25 * 0.25;
    const pointA = new THREE.Vector3();
    const pointB = new THREE.Vector3();
    const tangent = new THREE.Vector3();
    // The worker-ant GLB exports with its forward-facing axis on +Z (head
    // points toward +Z by default). Rotating from +Z to the tunnel tangent
    // pitches the body up/down along the curve instead of spinning the model
    // sideways.
    const MODEL_FORWARD = new THREE.Vector3(0, 0, 1);

    // Hostile mode: residents re-path toward the node closest to the player
    // every ~0.5 s. That keeps them inside the tunnels while still converging
    // on the intruder instead of beelining through rock.
    const nearestPlayerNode = this.hostile ? this.nearestNodeTo(playerLocalX, playerLocalZ) : -1;

    for (const r of this.residents) {
      if (r.hp <= 0) continue;

      if (this.hostile) {
        r.retargetT -= dt;
        if (r.retargetT <= 0) {
          const currentNode = r.pathIdx === 0 ? r.path[0] : r.path[r.pathIdx - 1];
          r.path = this.bfsPath(currentNode, nearestPlayerNode);
          r.pathIdx = Math.min(1, r.path.length - 1);
          r.t = 0;
          r.edgeLength = this.edgeLenFor(r.path, r.pathIdx);
          r.retargetT = 0.5;
        }
      }

      // Advance along the current edge's curve.
      if (r.pathIdx <= 0 || r.pathIdx >= r.path.length) {
        // Degenerate path — pick a fresh destination.
        const current = r.path[r.pathIdx] ?? r.path[r.path.length - 1] ?? 0;
        const dest = this.pickDistinctDestination(current);
        r.path = this.bfsPath(current, dest);
        r.pathIdx = Math.min(1, r.path.length - 1);
        r.t = 0;
        r.edgeLength = this.edgeLenFor(r.path, r.pathIdx);
        continue;
      }
      const from = r.path[r.pathIdx - 1];
      const to = r.path[r.pathIdx];
      const edge = this.getEdgeCurve(from, to);
      // Advance t by (speed × dt) / curveLength so speed is in metres/sec.
      r.t += (r.speed * dt) / Math.max(0.1, r.edgeLength);
      if (r.t >= 1) {
        r.t = 0;
        r.pathIdx++;
        if (r.pathIdx >= r.path.length) {
          const tail = r.path[r.path.length - 1];
          let dest: number;
          if (this.hostile) {
            dest = nearestPlayerNode;
          } else if (this.recalled) {
            const residentIdx = this.residents.indexOf(r);
            const target = this.entryGuardSet.has(residentIdx) ? 0 : this.queenNodeIdx;
            if (tail === target) {
              r.path = [target];
              r.pathIdx = 0;
              r.edgeLength = 1;
              continue;
            }
            dest = target;
          } else if (r.role === 'carrier') {
            // Carrier just arrived. Toggle state + re-path.
            if (r.carrierState === 'toDeposit') {
              this.depositEgg(tail);
              if (r.eggMesh) r.eggMesh.visible = false;
              r.carrierState = 'toQueen';
              dest = this.queenNodeIdx;
            } else {
              // Reached queen — pick up an egg and head for a random chamber.
              if (r.eggMesh) r.eggMesh.visible = true;
              r.carrierState = 'toDeposit';
              dest = this.pickDepositChamber();
            }
          } else {
            dest = this.pickDistinctDestination(tail);
          }
          r.path = this.bfsPath(tail, dest);
          r.pathIdx = Math.min(1, r.path.length - 1);
        }
        r.edgeLength = this.edgeLenFor(r.path, r.pathIdx);
      }

      // Sample the curve at the CURRENT t to place the resident inside the tube.
      if (edge) {
        const tEval = edge.forward ? r.t : (1 - r.t);
        edge.curve.getPointAt(Math.max(0, Math.min(1, tEval)), pointA);
        // Look-ahead a small step for yaw so the ant faces the direction of travel.
        const tAhead = edge.forward ? Math.min(1, tEval + 0.02) : Math.max(0, tEval - 0.02);
        edge.curve.getPointAt(tAhead, pointB);
        r.x = pointA.x;
        r.y = pointA.y + 0.05;
        r.z = pointA.z;
        r.mesh.position.set(r.x, r.y, ANT_Z_LIFT);
        // Orient along the 3-D tangent so the ant actually pitches up/down
        // along the tube instead of levitating horizontally through dirt.
        tangent.subVectors(pointB, pointA);
        if (tangent.lengthSq() > 1e-9) {
          tangent.normalize();
          r.mesh.quaternion.setFromUnitVectors(MODEL_FORWARD, tangent);
        }
      } else {
        // No curve (shouldn't happen); fall back to straight-line.
        const a = this.nodes[from].pos, b = this.nodes[to].pos;
        const t = r.t;
        r.x = a.x + (b.x - a.x) * t;
        r.y = a.y + (b.y - a.y) * t + 0.05;
        r.z = a.z + (b.z - a.z) * t;
        r.mesh.position.set(r.x, r.y, ANT_Z_LIFT);
      }

      if (r.hitPulseT > 0) {
        r.hitPulseT -= dt;
        const pulse = Math.max(0, r.hitPulseT) / 0.35;
        r.mesh.traverse((o) => {
          const m = o as THREE.Mesh;
          const mat = m.material as THREE.MeshStandardMaterial | undefined;
          if (mat && mat.emissive) mat.emissiveIntensity = 0.6 + 2.5 * pulse;
        });
      }
      if (this.hostile && r.kind === 'guard') {
        const mdx = r.x - playerLocalX;
        const mdz = r.z - playerLocalZ;
        if (mdx * mdx + mdz * mdz < BITE_R2) damageToPlayer += 4 * dt;
      }
    }
    this.tickQueenNurses(dt);
    this.tickFollowers(dt, playerLocalX, playerLocalZ);
    return damageToPlayer;
  }

  /** Queen-chamber egg shuttle. Each nurse walks from the queen to a random
   *  point in the chamber, drops an egg, walks back to the queen, repeat. */
  private tickQueenNurses(dt: number): void {
    if (this.queenNurses.length === 0) return;
    const queenNode = this.nodes[this.queenNodeIdx];
    const qx = queenNode.pos.x, qy = queenNode.pos.y, qz = queenNode.pos.z;
    const radius = queenNode.radius;
    for (const n of this.queenNurses) {
      if (n.hp <= 0) continue;
      const dx = n.tx - n.x;
      const dz = n.tz - n.z;
      const dist = Math.hypot(dx, dz);
      if (dist < 0.1) {
        if (n.mode === 'toDrop') {
          // Drop the egg HERE, then head back to the queen.
          this.addEggAt(n.x, queenNode.pos.y, n.z);
          if (n.eggMesh) n.eggMesh.visible = false;
          n.mode = 'toQueen';
          const ang = Math.random() * Math.PI * 2;
          const r = 0.35;
          n.tx = qx + Math.cos(ang) * r;
          n.tz = qz + Math.sin(ang) * r;
        } else {
          // Reached the queen — pick up a new egg, head for a fresh drop spot.
          if (n.eggMesh) n.eggMesh.visible = true;
          n.mode = 'toDrop';
          const ang = Math.random() * Math.PI * 2;
          const r = (0.5 + Math.random() * 0.5) * radius;
          n.tx = qx + Math.cos(ang) * r;
          n.tz = qz + Math.sin(ang) * r;
        }
      } else {
        const step = n.speed * dt;
        n.x += (dx / dist) * step;
        n.z += (dz / dist) * step;
      }
      n.mesh.position.set(n.x, qy + 0.1, ANT_Z_LIFT);
      n.mesh.rotation.y = Math.atan2(dx, dz);
    }
  }

  private nearestNodeTo(x: number, z: number): number {
    let bestI = 0;
    let bestD2 = Infinity;
    for (let i = 0; i < this.nodes.length; i++) {
      const n = this.nodes[i].pos;
      const dx = n.x - x, dz = n.z - z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; bestI = i; }
    }
    return bestI;
  }

  damageQueen(amount: number): boolean {
    if (this.queenDead) return false;
    this.queenHp -= amount;
    if (this.queenHp <= 0) { this.queenDead = true; this.queenHp = 0; return true; }
    return false;
  }

  damageResidentsNear(localX: number, localZ: number, radius: number, dps: number, dt: number): void {
    const r2 = radius * radius;
    for (const r of this.residents) {
      if (r.hp <= 0) continue;
      const dx = r.x - localX;
      const dz = r.z - localZ;
      if (dx * dx + dz * dz < r2) {
        r.hp -= dps * dt;
        r.hitPulseT = 0.35;
        if (r.hp <= 0) r.mesh.visible = false;
      }
    }
  }

  localToWorld(local: THREE.Vector3, out = new THREE.Vector3()): THREE.Vector3 {
    out.copy(local).add(this.group.position);
    return out;
  }

  show(): void { this.group.visible = true; }
  hide(): void { this.group.visible = false; }

  /** Extend the nest: pick a random existing leaf and spawn 1–2 new tunnels
   *  with their own small chambers. Called periodically by main.ts so the
   *  colony visibly grows over time. */
  grow(): void {
    // Collect leaf nodes (non-queen, non-entry, non-hub).
    const leaves: number[] = [];
    for (let i = 0; i < this.nodes.length; i++) {
      const n = this.nodes[i];
      if (n.kind === 'entry' || n.kind === 'hub' || n.kind === 'queen') continue;
      // Degree-1 nodes are true leaves of the tree.
      if (this.adjacency[i].length <= 1) leaves.push(i);
    }
    if (leaves.length === 0) return;
    const pIdx = leaves[Math.floor(Math.random() * leaves.length)];
    const parent = this.nodes[pIdx];
    const parentDir = new THREE.Vector3(0, -1, 0);
    if (this.adjacency[pIdx].length > 0) {
      const prev = this.nodes[this.adjacency[pIdx][0]].pos;
      parentDir.subVectors(parent.pos, prev).normalize();
    }
    const queenY = this.nodes[this.queenNodeIdx].pos.y;
    const grows = 1 + Math.floor(Math.random() * 2);
    for (let i = 0; i < grows; i++) {
      const yaw = Math.random() * Math.PI * 2;
      const pitchDown = 0.35 + Math.random() * 0.9;
      const length = 3 + Math.random() * 4;
      const dir = new THREE.Vector3(
        Math.cos(yaw) * Math.cos(pitchDown),
        -Math.sin(pitchDown),
        Math.sin(yaw) * Math.cos(pitchDown),
      ).normalize();
      dir.multiplyScalar(0.55).addScaledVector(parentDir, 0.45).normalize();
      const newPos = parent.pos.clone().addScaledVector(dir, length);
      // Never let growth dig a chamber below the queen's — she must always
      // occupy the deepest room.
      if (newPos.y <= queenY + 0.2) newPos.y = queenY + 0.3;
      const newKind: NodeRec['kind'] = Math.random() < 0.45 ? 'food' : 'leaf';
      const newR = newKind === 'food' ? 0.6 + Math.random() * 0.4 : 0.4 + Math.random() * 0.3;
      const newDepth = parent.depth + 1;
      const newIdx = this.addNode({ pos: newPos, depth: newDepth, kind: newKind, radius: newR });
      this.addEdgeTube(pIdx, newIdx, 0.26, newDepth);
      this.addChamberMeshes(newPos, newR, newDepth, this.getFloorMat());
      if (newKind === 'food') {
        this.addFoodPile(newPos.x, newPos.y + 0.01, newPos.z, 6 + Math.floor(Math.random() * 8));
      }
      // Demote the old leaf into a tunnel junction since it now has degree 2+.
      if (parent.kind === 'leaf') parent.kind = 'tunnel';
    }
  }

  private getFloorMat(): THREE.Material {
    return new THREE.MeshBasicMaterial({ color: 0x0a3416, side: THREE.FrontSide });
  }

  /** Snapshot for the minimap's side-view renderer. Positions are LOCAL
   *  (relative to nest.x, 0, nest.z) so the caller can project freely. */
  sideViewData(): {
    nodes: Array<{ x: number; y: number; z: number; kind: NodeRec['kind']; radius: number }>;
    edges: Array<[number, number]>;
    playerLocal: { x: number; y: number; z: number };
    bounds: { minX: number; maxX: number; minY: number; maxY: number };
  } {
    const nodes = this.nodes.map((n) => ({
      x: n.pos.x, y: n.pos.y, z: n.pos.z, kind: n.kind, radius: n.radius,
    }));
    const edges: Array<[number, number]> = [];
    for (let i = 0; i < this.adjacency.length; i++) {
      for (const j of this.adjacency[i]) if (j > i) edges.push([i, j]);
    }
    let minX = 0, maxX = 0, minY = 0, maxY = 0;
    for (const n of nodes) {
      const r = Math.hypot(n.x, n.z) * Math.sign(n.x || 1);
      if (r < minX) minX = r; if (r > maxX) maxX = r;
      if (n.y < minY) minY = n.y; if (n.y > maxY) maxY = n.y;
    }
    return {
      nodes,
      edges,
      playerLocal: {
        x: this.playerMarker.position.x,
        y: this.playerMarker.position.y,
        z: this.playerMarker.position.z,
      },
      bounds: { minX: Math.min(minX, -4), maxX: Math.max(maxX, 4), minY: Math.min(minY, -10), maxY: 0.5 },
    };
  }
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function (): number {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
